import { User } from '../types'
import { AuthTokens, LoginResponse } from '../hooks/useAuth'

// Built-in super admin (hidden from Employees / User Management lists).
// Password is fixed and hardcoded by design.
export const SUPER_ADMIN_EMAIL = 'superadmin@workbeam.com'
export const SUPER_ADMIN_PASSWORD = 'WorkbeamSuper@2026'
const DEFAULT_MOCK_PASSWORD = 'password123'

// Per-user password overrides. Any email not listed here falls back to the
// default mock password.
const mockPasswords: Record<string, string> = {
  [SUPER_ADMIN_EMAIL]: SUPER_ADMIN_PASSWORD,
}

// Mock data storage - Unified users/employees
const mockUsers: User[] = [
  {
    id: 'super-admin',
    email: SUPER_ADMIN_EMAIL,
    name: 'Super Admin',
    role: 'super_admin',
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    status: 'active',
    lastActive: '2024-01-15T10:30:00Z',
    isEmployee: false,
    hasLoginAccess: true,
  } as User,
  {
    id: '1',
    email: 'admin@workbeam.com',
    name: 'John Doe',
    role: 'admin',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    
    // Employee-specific fields
    employeeCode: 'EMP001',
    designation: 'Senior Software Engineer',
    department: 'Engineering',
    status: 'active',
    dateOfJoining: '2023-01-15',
    phone: '+1-555-0123',
    address: '123 Main St, New York, NY 10001',
    salary: 85000,
    lastActive: '2024-01-15T10:30:00Z',
    
    // Authentication fields
    isEmployee: true,
    hasLoginAccess: true
  },
  {
    id: '2',
    email: 'manager@workbeam.com',
    name: 'Jane Smith',
    role: 'manager',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
    createdAt: '2023-03-20T00:00:00Z',
    updatedAt: '2024-01-15T09:15:00Z',
    
    // Employee-specific fields
    employeeCode: 'EMP002',
    designation: 'Marketing Manager',
    department: 'Marketing',
    status: 'active',
    dateOfJoining: '2023-03-20',
    phone: '+1-555-0124',
    address: '456 Oak Ave, Los Angeles, CA 90210',
    managerId: '1',
    managerName: 'John Doe',
    salary: 75000,
    lastActive: '2024-01-15T09:15:00Z',
    
    // Authentication fields
    isEmployee: true,
    hasLoginAccess: true
  },
  {
    id: '3',
    email: 'employee@workbeam.com',
    name: 'Bob Johnson',
    role: 'employee',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    createdAt: '2023-06-10T00:00:00Z',
    updatedAt: '2024-01-15T08:45:00Z',
    
    // Employee-specific fields
    employeeCode: 'EMP003',
    designation: 'Sales Representative',
    department: 'Sales',
    status: 'active',
    dateOfJoining: '2023-06-10',
    phone: '+1-555-0125',
    address: '789 Pine St, Chicago, IL 60601',
    managerId: '2',
    managerName: 'Jane Smith',
    salary: 55000,
    lastActive: '2024-01-15T08:45:00Z',
    
    // Authentication fields
    isEmployee: true,
    hasLoginAccess: true
  },
  {
    id: '4',
    email: 'contractor@workbeam.com',
    name: 'Alice Brown',
    role: 'employee',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
    createdAt: '2023-02-28T00:00:00Z',
    updatedAt: '2023-12-20T16:30:00Z',
    
    // This is a contractor - employee but no login access
    employeeCode: 'EMP004',
    designation: 'HR Specialist',
    department: 'Human Resources',
    status: 'inactive',
    dateOfJoining: '2023-02-28',
    phone: '+1-555-0126',
    address: '321 Elm St, Houston, TX 77001',
    managerId: '1',
    managerName: 'John Doe',
    salary: 60000,
    lastActive: '2023-12-20T16:30:00Z',
    
    // Authentication fields
    isEmployee: true,
    hasLoginAccess: false
  },
  {
    id: '5',
    email: 'guest@workbeam.com',
    name: 'Mike Wilson',
    role: 'employee',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
    createdAt: '2023-08-15T00:00:00Z',
    updatedAt: '2024-01-15T11:00:00Z',
    
    // This is a guest user - not an employee, but has login access
    status: 'active',
    lastActive: '2024-01-15T11:00:00Z',
    
    // Authentication fields
    isEmployee: false,
    hasLoginAccess: true
  }
]

// Mock tokens storage
const mockTokens = new Map<string, AuthTokens>()

// Static lookup data
export const STATIC_DATA = {
  departments: [
    'Engineering',
    'Marketing',
    'Sales',
    'Human Resources',
    'Finance',
    'Operations',
    'Customer Support',
    'Product Management',
    'Design',
    'Legal',
    'IT Support',
    'Research & Development'
  ],
  
  designations: [
    'Software Engineer',
    'Senior Software Engineer',
    'Lead Software Engineer',
    'Engineering Manager',
    'Marketing Manager',
    'Marketing Specialist',
    'Sales Representative',
    'Sales Manager',
    'HR Specialist',
    'HR Manager',
    'Financial Analyst',
    'Finance Manager',
    'Operations Manager',
    'Customer Support Representative',
    'Product Manager',
    'Product Owner',
    'UI/UX Designer',
    'Graphic Designer',
    'Legal Counsel',
    'IT Support Specialist',
    'Research Scientist',
    'Data Analyst',
    'Project Manager',
    'Business Analyst',
    'Content Writer',
    'Social Media Manager'
  ],
  
  roles: [
    'employee',
    'manager',
    'admin'
  ],
  
  employeeStatuses: [
    'active',
    'inactive',
    'terminated',
    'on_leave'
  ],
  
  leaveTypes: [
    'annual',
    'sick',
    'personal',
    'maternity',
    'paternity',
    'bereavement',
    'unpaid'
  ],
  
  leaveStatuses: [
    'pending',
    'approved',
    'rejected',
    'cancelled'
  ],
  
  timesheetStatuses: [
    'draft',
    'submitted',
    'approved',
    'rejected'
  ],
  
  attendanceStatuses: [
    'checked-in',
    'checked-out',
    'not-checked-in',
    'late',
    'absent',
    'half-day'
  ],
  
  notificationTypes: [
    'info',
    'success',
    'warning',
    'error'
  ],
  
  projectStatuses: [
    'active',
    'completed',
    'on-hold',
    'cancelled'
  ],
  
  documentTypes: [
    'pdf',
    'doc',
    'docx',
    'jpg',
    'png',
    'xlsx',
    'ppt',
    'other'
  ],
  
  countries: [
    'United States',
    'Canada',
    'United Kingdom',
    'Germany',
    'France',
    'Australia',
    'Japan',
    'India',
    'Brazil',
    'Mexico'
  ],
  
  timezones: [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Australia/Sydney'
  ],
  
  currencies: [
    'USD',
    'EUR',
    'GBP',
    'JPY',
    'CAD',
    'AUD',
    'CHF',
    'CNY',
    'INR',
    'BRL'
  ],
  
  languages: [
    'English',
    'Spanish',
    'French',
    'German',
    'Chinese',
    'Japanese',
    'Korean',
    'Arabic',
    'Russian',
    'Portuguese'
  ]
}

// Simulate network delay
const delay = (ms: number = 1000) => new Promise(resolve => setTimeout(resolve, ms))

// Generate mock tokens
const generateTokens = (userId: string): AuthTokens => {
  const now = Date.now()
  return {
    accessToken: `mock-access-token-${userId}-${now}`,
    refreshToken: `mock-refresh-token-${userId}-${now}`,
    expiresIn: now + (15 * 60 * 1000) // 15 minutes
  }
}

// Mock API class
export class MockApi {
  private static instance: MockApi
  private isEnabled: boolean = false

  private constructor() {}

  static getInstance(): MockApi {
    if (!MockApi.instance) {
      MockApi.instance = new MockApi()
    }
    return MockApi.instance
  }

  enable() {
    this.isEnabled = true
    console.log('🔧 Mock API enabled')
  }

  disable() {
    this.isEnabled = false
    console.log('🔧 Mock API disabled')
  }

  isMockEnabled(): boolean {
    return this.isEnabled
  }

  // Authentication endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    await delay(800) // Simulate network delay

    // Find user by email
    const user = mockUsers.find(u => u.email === email)
    
    if (!user) {
      throw new Error('No account found with this email address')
    }

    // Simple password validation (for demo purposes). Specific users (e.g. the
    // built-in super admin) have a fixed override password.
    const expected = mockPasswords[user.email] ?? DEFAULT_MOCK_PASSWORD
    if (password !== expected) {
      throw new Error('Invalid email or password')
    }

    // Generate tokens
    const tokens = generateTokens(user.id)
    mockTokens.set(user.id, tokens)

    console.log('🔧 Mock API: Login successful', { email, userId: user.id })

    return {
      user,
      tokens
    }
  }

  async logout(refreshToken: string): Promise<void> {
    await delay(300)

    // Find and remove tokens
    for (const [userId, tokens] of mockTokens.entries()) {
      if (tokens.refreshToken === refreshToken) {
        mockTokens.delete(userId)
        break
      }
    }

    console.log('🔧 Mock API: Logout successful')
  }

  async refreshToken(refreshToken: string): Promise<{ tokens: AuthTokens }> {
    await delay(500)

    // Find user by refresh token
    let userId: string | null = null
    for (const [id, tokens] of mockTokens.entries()) {
      if (tokens.refreshToken === refreshToken) {
        userId = id
        break
      }
    }

    if (!userId) {
      throw new Error('Invalid refresh token')
    }

    // Generate new tokens
    const newTokens = generateTokens(userId)
    mockTokens.set(userId, newTokens)

    console.log('🔧 Mock API: Token refresh successful', { userId })

    return {
      tokens: newTokens
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await delay(1000)

    const user = mockUsers.find(u => u.email === email)
    
    if (!user) {
      throw new Error('No account found with this email address')
    }

    console.log('🔧 Mock API: Password reset email sent', { email })
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await delay(800)

    // Simple token validation (in real app, this would validate the reset token)
    if (!token || token.length < 10) {
      throw new Error('Invalid or expired reset token')
    }

    // Validate password strength
    if (password.length < 8) {
      throw new Error('Password must be at least 8 characters long')
    }

    console.log('🔧 Mock API: Password reset successful', { token: token.substring(0, 10) + '...' })
  }

  // User management endpoints
  async updateProfile(userId: string, updates: Partial<User>): Promise<User> {
    await delay(600)

    const userIndex = mockUsers.findIndex(u => u.id === userId)
    if (userIndex === -1) {
      throw new Error('User not found')
    }

    // Update user data
    mockUsers[userIndex] = { ...mockUsers[userIndex], ...updates }

    console.log('🔧 Mock API: Profile updated', { userId, updates })

    return mockUsers[userIndex]
  }

  async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<void> {
    await delay(700)

    const user = mockUsers.find(u => u.id === userId)
    if (!user) {
      throw new Error('User not found')
    }

    // Validate current password
    if (currentPassword !== 'password123') {
      throw new Error('Current password is incorrect')
    }

    // Validate new password strength
    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters long')
    }

    console.log('🔧 Mock API: Password changed successfully', { userId })
  }

  // Dashboard data endpoints
  async getDashboardStats(): Promise<any> {
    await delay(500)

    return {
      totalEmployees: 45,
      presentToday: 38,
      onLeave: 5,
      lateArrivals: 2,
      recentActivities: [
        { id: 1, type: 'clock_in', user: 'John Doe', time: '09:00 AM' },
        { id: 2, type: 'leave_request', user: 'Jane Smith', time: '08:30 AM' },
        { id: 3, type: 'clock_out', user: 'Bob Johnson', time: '05:30 PM' }
      ]
    }
  }

  async getEmployees(): Promise<any[]> {
    await delay(800)

    return [
      { id: '1', name: 'John Doe', email: 'john@workbeam.com', role: 'admin', status: 'active' },
      { id: '2', name: 'Jane Smith', email: 'jane@workbeam.com', role: 'manager', status: 'active' },
      { id: '3', name: 'Bob Johnson', email: 'bob@workbeam.com', role: 'employee', status: 'active' },
      { id: '4', name: 'Alice Brown', email: 'alice@workbeam.com', role: 'employee', status: 'inactive' }
    ]
  }

  // Static data endpoints
  async getStaticData(): Promise<typeof STATIC_DATA> {
    await delay(300)
    return STATIC_DATA
  }

  async getDepartments(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.departments
  }

  async getDesignations(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.designations
  }

  async getRoles(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.roles
  }

  async getLeaveTypes(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.leaveTypes
  }

  async getLeaveStatuses(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.leaveStatuses
  }

  async getTimesheetStatuses(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.timesheetStatuses
  }

  async getAttendanceStatuses(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.attendanceStatuses
  }

  async getNotificationTypes(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.notificationTypes
  }

  async getProjectStatuses(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.projectStatuses
  }

  async getDocumentTypes(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.documentTypes
  }

  async getCountries(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.countries
  }

  async getTimezones(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.timezones
  }

  async getCurrencies(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.currencies
  }

  async getLanguages(): Promise<string[]> {
    await delay(200)
    return STATIC_DATA.languages
  }

  // Utility methods
  getMockUsers(): User[] {
    return [...mockUsers]
  }

  getMockTokens(): Map<string, AuthTokens> {
    return new Map(mockTokens)
  }

  getStaticDataSync(): typeof STATIC_DATA {
    return STATIC_DATA
  }

  clearMockData(): void {
    mockTokens.clear()
    console.log('🔧 Mock API: Data cleared')
  }

  // Simulate network errors
  async simulateError(errorType: 'network' | 'server' | 'timeout'): Promise<never> {
    await delay(500)

    switch (errorType) {
      case 'network':
        throw new Error('Network error: Unable to connect to server')
      case 'server':
        throw new Error('Server error: Internal server error (500)')
      case 'timeout':
        throw new Error('Request timeout: Server took too long to respond')
      default:
        throw new Error('Unknown error occurred')
    }
  }
}

// Export singleton instance
export const mockApi = MockApi.getInstance()

// Note: Mock API interceptor function removed as it's not used in current implementation
